import { useEffect, useRef, useState } from 'react';
import { Expand, X } from 'lucide-react';
import { getImageUrl } from '../../../utils/getImageUrl';

export default function RenderedPagePanel({ path, language, busy, onRemove }) {
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const pairLabel = (language || '').replace(/_/g, '–').toUpperCase();
  useEffect(() => {
    if (!dialog) return undefined;
    const previous = document.activeElement;
    dialogRef.current?.querySelector('button')?.focus();
    return () => previous?.focus();
  }, [dialog]);
  const remove = async () => {
    setError('');
    if (await onRemove()) setDialog(null);
    else setError('Could not remove the rendered page. Please try again.');
  };
  return <section className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
    <div className="flex items-center justify-between gap-5">
      <div>
        <h1 className="text-lg font-semibold text-slate-950">Rendered page <span className="ml-2 text-sm font-medium text-slate-500">{pairLabel}</span></h1>
        <p className="mt-1 text-xs text-slate-500">{path ? 'Saved output · click to enlarge' : 'No rendered page saved'}</p>
      </div>
      {path ? <div className="relative shrink-0">
        <button type="button" onClick={() => setDialog('image')} aria-label={`Enlarge rendered page ${pairLabel}`} className="group relative block overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-1 shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <img src={getImageUrl(path)} alt={`Rendered page ${pairLabel}`} className="h-24 w-20 object-contain" />
          <span className="absolute bottom-1 right-1 rounded bg-white/90 p-1 text-slate-600"><Expand size={12} /></span>
        </button>
        <button type="button" disabled={busy} onClick={() => { setError(''); setDialog('remove'); }} aria-label={`Remove rendered page ${pairLabel}`} title="Remove rendered page" className="absolute -right-2 -top-2 rounded-full border border-slate-200 bg-white p-1 text-slate-500 shadow-sm hover:bg-red-50 hover:text-red-700 disabled:opacity-40"><X size={14} /></button>
      </div> : <div className="flex h-24 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm font-medium text-slate-400">N/A</div>}
    </div>
    {dialog && <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-950/60 p-5">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="rendered-page-dialog-heading" onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) setDialog(null);
        if (event.key === 'Tab') {
          const buttons = [...event.currentTarget.querySelectorAll('button:not(:disabled)')];
          if (document.activeElement === (event.shiftKey ? buttons[0] : buttons[buttons.length - 1])) {
            event.preventDefault(); (event.shiftKey ? buttons[buttons.length - 1] : buttons[0])?.focus();
          }
        }
      }} className={`flex max-h-[92vh] flex-col rounded-2xl bg-white shadow-2xl ${dialog === 'image' ? 'max-w-[95vw]' : 'w-full max-w-md'}`}>
        <div className="flex items-center justify-between gap-5 border-b border-slate-100 px-5 py-3">
          <h2 id="rendered-page-dialog-heading" className="font-semibold text-slate-900">{dialog === 'remove' ? 'Remove rendered page?' : 'Rendered page'} · {pairLabel}</h2>
          <button type="button" disabled={busy} onClick={() => setDialog(null)} aria-label="Close rendered page dialog" className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><X size={18} /></button>
        </div>
        {dialog === 'image' ? <div className="min-h-0 overflow-auto bg-slate-50 p-3"><img src={getImageUrl(path)} alt={`Enlarged rendered page ${pairLabel}`} className="max-h-[78vh] w-auto max-w-full object-contain" /></div>
          : <div className="space-y-4 p-5">
            <p className="text-sm text-slate-600">Remove the saved rendered page for {pairLabel}? Your editable layout, text, and artwork will stay.</p>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" disabled={busy} onClick={() => setDialog(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-40">Cancel</button>
              <button type="button" disabled={busy} onClick={remove} className="rounded-lg bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">{busy ? 'Removing…' : 'Remove rendered page'}</button>
            </div>
          </div>}
      </div>
    </div>}
  </section>;
}
